import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { LOCATIONS, NAV_LINKS, SERVICES, SITE } from "../data/site";

export default function Footer() {
  return (
    <footer className="bg-gig-black text-white" data-testid="site-footer">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-5 py-12 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <h2 className="font-condensed text-3xl md:text-4xl font-bold uppercase leading-[0.95] max-w-xl">
            Let&apos;s Build Something <span className="text-gig-red">Great Together.</span>
          </h2>
          <div className="flex flex-wrap items-center gap-4">
            <a href={SITE.phoneHref} className="btn-primary" data-testid="footer-phone-cta">
              <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} className="btn-outline-light" data-testid="footer-email-cta">
              <Mail className="h-4 w-4" aria-hidden="true" /> {SITE.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main columns */}
      <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-14 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_0.8fr_1fr] lg:px-10">
        <div>
          <img src={SITE.logoWhite} alt="Global Industry Group" className="h-10 w-auto" width="2000" height="412" loading="lazy" />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
            Global Industry Group is a multi-discipline industrial contractor delivering
            construction, maintenance, and specialty services for industrial, manufacturing,
            energy, and mission-critical facilities throughout Texas.
          </p>
        </div>

        <nav aria-label="Footer services">
          <h3 className="font-condensed text-sm font-semibold uppercase tracking-[0.22em] text-gig-red">
            Services
          </h3>
          <ul className="mt-4 space-y-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-sm text-white/70 hover:text-white transition-colors"
                  data-testid={`footer-service-${s.slug}`}
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Footer company">
          <h3 className="font-condensed text-sm font-semibold uppercase tracking-[0.22em] text-gig-red">
            Company
          </h3>
          <ul className="mt-4 space-y-2">
            {NAV_LINKS.filter((l) => ["About Us", "Projects", "Safety & Quality", "Careers", "Contact Us"].includes(l.label)).map((l) => (
              <li key={l.path}>
                <Link to={l.path} className="text-sm text-white/70 hover:text-white transition-colors" data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/industries" className="text-sm text-white/70 hover:text-white transition-colors" data-testid="footer-industries">
                Industries
              </Link>
            </li>
          </ul>
        </nav>

        <div>
          <h3 className="font-condensed text-sm font-semibold uppercase tracking-[0.22em] text-gig-red">
            Locations
          </h3>
          <ul className="mt-4 space-y-5">
            {LOCATIONS.map((loc) => (
              <li key={loc.id} data-testid={`footer-location-${loc.id}`}>
                <p className="font-condensed text-base font-semibold uppercase tracking-[0.08em]">{loc.label}</p>
                <p className="mt-1 text-sm text-white/70">
                  {loc.lines[0]}
                  <br />
                  {loc.lines[1]}
                </p>
                <a
                  href={loc.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-gig-red hover:text-white transition-colors"
                  data-testid={`footer-directions-${loc.id}`}
                >
                  Get Directions <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-3 px-5 py-6 text-xs text-white/50 sm:flex-row lg:px-10">
          <p data-testid="footer-copyright">© 2026 Global Industry Group, Inc. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-white transition-colors" data-testid="footer-terms">
              Terms of Use
            </Link>
            <a href="/sitemap.xml" className="hover:text-white transition-colors" data-testid="footer-sitemap">
              Sitemap
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
